import type { AwsSsoConnectionApi } from '../components/aws-import/AwsSsoConnectionWizard'
import type { AwsConnectionOutput, AwsConnectionPayload } from './types'

/**
 * API client for the PUBLIC /newcustomer onboarding flow. Deliberately does
 * NOT go through api/core.ts: there is no signed-in user, so no bearer token
 * is attached and a 401 must surface as an inline error (an expired
 * onboarding token), never a redirect to /login.
 *
 * Everything after the initial create carries the signed onboarding token in
 * `X-Onboarding-Token` — the backend scopes each call to the company the
 * token was minted for.
 */

async function publicRequest<T>(path: string, init?: RequestInit, token?: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (token) headers['X-Onboarding-Token'] = token
  const resp = await fetch(path, { ...init, headers })
  if (!resp.ok) {
    const body = await resp.text()
    let detail = body
    try {
      detail = JSON.parse(body)?.detail ?? body
    } catch {
      // non-JSON error body — use as-is
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
  }
  return resp.json() as Promise<T>
}

export interface StartOnboardingResult {
  slug: string
  token: string
}

export const publicOnboardingApi = {
  /** Create the company from the customer-typed profile; returns the token
   *  that scopes the rest of the flow. */
  start: (companyUrl: string, values: Record<string, unknown>) =>
    publicRequest<StartOnboardingResult>('/api/public/onboarding', {
      method: 'POST',
      body: JSON.stringify({ company_url: companyUrl, values }),
    }),

  saveConnection: (token: string, payload: AwsConnectionPayload) =>
    publicRequest<AwsConnectionOutput>(
      '/api/public/onboarding/connection',
      { method: 'PUT', body: JSON.stringify(payload) },
      token,
    ),

  testCredentials: (
    token: string,
    accessKey: string,
    secretKey: string,
    expectedAccountId?: string,
    sessionToken?: string,
    awsAccountId?: string,
  ) =>
    publicRequest<{
      ok: boolean
      account_id: string | null
      arn: string | null
      user_id: string | null
      reason: string | null
      expired: boolean
      source?: 'entered' | 'stored'
    }>(
      '/api/public/onboarding/connection/test',
      {
        method: 'POST',
        body: JSON.stringify({
          access_key: accessKey,
          secret_key: secretKey,
          expected_account_id: expectedAccountId,
          session_token: sessionToken,
          aws_account_id: awsAccountId,
        }),
      },
      token,
    ),

  runScan: (token: string) =>
    publicRequest<{ ok: boolean; status: string }>(
      '/api/public/onboarding/scan',
      { method: 'POST' },
      token,
    ),

  skipAws: (token: string) =>
    publicRequest<{ ok: boolean }>('/api/public/onboarding/skip', { method: 'POST' }, token),

  /** Token-gated mirror of the /api/aws-sso device-flow endpoints, shaped for
   *  AwsSsoConnectionWizard's injectable api prop. */
  ssoApi: (token: string): AwsSsoConnectionApi => ({
    start: (startUrl, region) =>
      publicRequest(
        '/api/public/onboarding/sso/start',
        { method: 'POST', body: JSON.stringify({ start_url: startUrl, region }) },
        token,
      ),
    token: (p) =>
      publicRequest(
        '/api/public/onboarding/sso/token',
        {
          method: 'POST',
          body: JSON.stringify({
            client_id: p.clientId,
            client_secret: p.clientSecret,
            device_code: p.deviceCode,
            region: p.region,
          }),
        },
        token,
      ),
    accounts: (accessToken, region) =>
      publicRequest(
        '/api/public/onboarding/sso/accounts',
        { method: 'POST', body: JSON.stringify({ access_token: accessToken, region }) },
        token,
      ),
    credentials: (accessToken, region, accountId, roleName) =>
      publicRequest(
        '/api/public/onboarding/sso/credentials',
        {
          method: 'POST',
          body: JSON.stringify({
            access_token: accessToken,
            region,
            account_id: accountId,
            role_name: roleName,
          }),
        },
        token,
      ),
    provisionAccessKeys: (p) =>
      publicRequest(
        '/api/public/onboarding/sso/provision-access-keys',
        {
          method: 'POST',
          body: JSON.stringify({
            temp_access_key: p.tempAccessKey,
            temp_secret_key: p.tempSecretKey,
            temp_session_token: p.tempSessionToken,
            account_id: p.accountId,
            expiration_days: p.expirationDays,
          }),
        },
        token,
      ),
  }),
}
